import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useServices } from "@/services-context";
import styles from "./product-page-mobile.module.scss";
import { features } from "@/constants/features";
import { LeadFormCondition, ProductStateType } from "@/util/constants";
import FixBreadcrumb from "@/elements-react/fix-breadcrumb";
import FixText from "@/elements-react/fix-text";
import { FixAlert } from "@/elements-react/fix-alert";
import { ProductPageStore, useProductPageCommon } from "@/pages-react/product-page/ProductPageCommon.tsx";
import { LayoutStyleTypes, LayoutTypes } from "@/util-react/layout.tsx";
import FixImage from "@/elements-react/fix-image";
import SpecificationsList from "@/elements-react/specifications-list";
import { BenefitsList } from "@/elements-react/benefits-list";
import ProductMpn from "@/elements-react/product-mpn";
import { createProductUrl } from "@/util/category.ts";
import GoToReplacementButton from "@/elements-react/go-to-replacement-button";
import { useEffect } from "preact/hooks";
import { PageTags } from "@/elements-react/document-elements/page-tags";
import { SuggestPriceButton } from "@/elements-react/suggest-price-button";
import LeadForm from "@/pages-react/product-page/lead-form";
import { MessengersBlock } from "@/pages-react/product-page/messangers-block/MessengersBlock.tsx";

const ProductPageMobile = (props: { store: ProductPageStore }) => {
  const { t } = useTranslation();
  const services = useServices();

  const {
    redirectMpn,
    noImageProductUrl,
    title,
    mpn,
    showRating,
    showFits,
    product,
    stockStatus,
    specifications,
    jsonLdDataset,
    metaDataset,
    seoMarkupDataset,
    hasBlock,
    renderDispatch,
    renderAdd,
    renderQuantity,
    getHasAvailableInStock,
    renderProductDiscontinuedSegment,
    renderCanBe,
    renderDiscount,
    renderPrice,
    renderTip,
    renderShipping,
    renderShippingRates,
    renderAdditionalBlock,
    renderStatus,
    renderBadge,
    renderVat,
    renderAbout,
    renderSituable,
    renderCarousel,
    configureSelections,
    renderTitle,
    getDataLeadForm,
    hasProperty,
  } = useProductPageCommon(LayoutTypes.TYPE_MOBILE, props.store);

  useEffect(() => configureSelections(product, null), []);

  const situableFor = hasBlock("situable-for");
  const canBe = hasBlock("can-be");
  const carousel = hasBlock("carousel");
  const dispatch = hasBlock("product-dispatch");
  const discount = hasBlock("discount");
  const quantityInput = hasBlock("quantity-input");
  const quantityTip = hasBlock("quantity-tip");
  const shipping = hasBlock("shipping-notify");

  const isDiscontinuedRender = product && product.exchangesFinal?.length > 0 && !getHasAvailableInStock();

  const showCrossesForDiscontinued = product?.state === ProductStateType.Discontinued;

  // Render helpers
  const renderTopSegment = () => (
    <div className={classNames(styles["top-group"], styles.box)}>
      <div className={styles.alerts}>
        <FixAlert />
      </div>
      {redirectMpn && (
        <div className={styles.notification}>
          <div className={styles["success-notify"]}>
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  "43ook0potrz3ufhv.looks-like",
                  "Looks like product <b>{{mpn}}</b> is no longer available. We’ve found a replacement from the manufacturer and redirected you here",
                  { mpn: redirectMpn },
                ),
              }}
            />
          </div>
        </div>
      )}
      <div className={styles.navigation}>
        <FixBreadcrumb />
      </div>
      <div className={styles.title}>{title}</div>
      <div className={styles.indicators}>
        {/*ToDo need to define exactly appearance of status block */}
        <div>{!hasProperty("onRequest") && !hasProperty("noPrice") && renderStatus(LayoutStyleTypes.TYPE_SM)}</div>
        <ProductMpn code={mpn} />
      </div>
    </div>
  );

  const renderProductPricesSegment = () => (
    <div className={styles["price-cart-group"]}>
      {dispatch && renderDispatch()}
      <div className={styles.middle}>
        <div className={styles.left}>
          {discount && features.discount && (
            <div className={styles.row}>
              {/* renderDiscount() */} {/* renderBadge() */}
            </div>
          )}
          <div>
            {renderPrice(true, styles.price)}
            <div className={styles.tips}>{quantityTip && renderTip(styles["quantity-tip"])}</div>
          </div>
        </div>
        <div className={styles.right}>
          <div>{renderVat()}</div>
          <div>{shipping && !isDiscontinuedRender && renderShipping()}</div>
        </div>
      </div>
      <div className={styles.bottom}>
        {renderAdd()}
        <div className={styles["quantity"]}>{quantityInput && renderQuantity()}</div>
      </div>
      {renderAdditionalBlock()}
    </div>
  );

  const renderDiscontinuedStateDiscontinued = () => {
    const descriptionText = t(
      "na780sifugbasnis.discontinued-text",
      "The official replacement from [{{product}}] is [{{exchange}}], designed to meet the same needs",
      {
        product: product?.mpnFormatted,
        exchange: product?.exchangesFinal?.[0]?.mpnFormatted,
      },
    );
    const productUrl = createProductUrl(product?.exchangesFinal?.[0], services.categories, "long", product!.mpnFormatted);
    return (
      <div className={styles["content-segment"]} style={{ marginBottom: 0, paddingBottom: 0 }}>
        <h2 className={styles.title}>{t("ansd80oishdnsaid.product-is-no-produced", "This product is no longer produced")}</h2>
        <FixText type={LayoutStyleTypes.TYPE_LG} text={descriptionText} />
        <GoToReplacementButton productUrl={productUrl} />
      </div>
    );
  };

  // Old on request template
  // const renderDiscontinuedStateOnRequest = () => {
  // const descriptionText = `${t(
  //   "na780sab3fe89aia.send-request",
  //   "To send a request, write to",
  // )} <a href="mailto:sales@fixparts-online.com">sales@fixparts-online.com</a>`;
  // return (
  //   <div className={styles["content-segment"]} style={{ marginBottom: 0, paddingBottom: 0 }}>
  //     <h2 className={styles.title}>{t("na780ol8ak4lhf9a.product-on-request", "This product is on request")}</h2>
  //     <FixText type={LayoutStyleTypes.TYPE_LG} text={descriptionText} />
  //   </div>
  // );
  // };

  const renderLeadForm = () => {
    const dataLeadForm = getDataLeadForm();

    if (!dataLeadForm) {
      return;
    }

    return (
      <div className={classNames(styles.leadContainer)}>
        {hasProperty("hasPrice") && (
          <div>
            {renderPrice(true, styles.price)}
            <div className={styles["last-price-text"]}>{t("ab7r8gowieugrfbr.last-known-price", "Last known price — may change upon restock")}</div>
          </div>
        )}
        {renderTitle() && <div className={classNames(styles.title)}>{renderTitle()}</div>}
        <LeadForm
          title={dataLeadForm.title}
          subTitle={dataLeadForm.subTitle}
          buttonText={dataLeadForm.buttonText}
          formName={dataLeadForm.formName}
          mpn={mpn}
          customize={{ buttonIcon: dataLeadForm.buttonIcon }}
          formAttributes={{
            isHiddePriceIfProductIsOutOfStock: hasProperty("isHidePriceIfProductIsOutOfStock") ? "yes" : "no",
            deliveryStandardTimesIsNotDefined: hasProperty("deliveryStandardTimesIsNotDefined") ? "yes" : "no",
            hasPrice: hasProperty("hasPrice") ? "yes" : "no",
          }}
        />
        <MessengersBlock productMpn={mpn} />
      </div>
    );
  };

  const renderCrosses = () => (
    <div className={styles["content-segment"]}>
      <h2 className={styles.title}>{t("c3d98ff5.analogues", "Analogues")}</h2>
      {renderCanBe(LayoutStyleTypes.TYPE_SM)}
    </div>
  );

  const renderCrossesForDiscontinued = () => (
    <div className={styles["content-segment"]}>
      <div className={styles["arrow-up"]} />
      <div className={styles.discontinued}>
        <h2 className={styles.title}>{t("abr78vba78k3ufhv.great-analogues", "Great analogues for this product:")}</h2>
        {renderCanBe(LayoutStyleTypes.TYPE_SM)}
      </div>
    </div>
  );

  const aboutContent = (
    <>
      {hasBlock("about") && (
        <div className={classNames(styles["content-segment"], styles.compact)}>
          <h2 className={styles["title"]}>{t("afp6t38xk75sr191.about", "About")}</h2>
          {renderAbout(LayoutStyleTypes.TYPE_SM)}
        </div>
      )}
      {hasBlock("situable-for") && (
        <div className={classNames(styles["content-segment"], styles.compact)}>
          <h2 className={styles["title"]}>{t("4d431o9n9ol327o8.suitable-summary", "Suitable for")}</h2>
          <div>{renderSituable(LayoutStyleTypes.TYPE_SM)}</div>
        </div>
      )}
    </>
  );

  const isLeadFormState = hasProperty("onRequest") || hasProperty("noPrice") || hasProperty("outOfStock");
  const shouldRenderShippingRates =
    services.customerChannel.showShippingRates &&
    shipping &&
    !isDiscontinuedRender &&
    !isLeadFormState &&
    product.state !== ProductStateType.Discontinued;

  const actionSegment = (() => {
    if (isLeadFormState) {
      return renderLeadForm();
    } else if (product.state === ProductStateType.Discontinued) {
      return renderDiscontinuedStateDiscontinued();
    } else {
      return renderProductPricesSegment();
    }
  })();

  return (
    <>
      <div className={styles["content-segment"]}>
        {renderTopSegment()}
        {carousel ? (
          renderCarousel(LayoutStyleTypes.TYPE_SM)
        ) : (
          <div className={styles["no-image-block"]}>
            <FixImage className="card" type={LayoutStyleTypes.TYPE_LG} src={noImageProductUrl} aspect={1.3} />
            <div className={styles["mpn-block"]}>{product?.mpn}</div>
          </div>
        )}
        {actionSegment}
        {/*{featuresProp.product_variations && renderVariations()}*/}
      </div>
      {canBe && features.product_cross && (showCrossesForDiscontinued ? renderCrossesForDiscontinued() : renderCrosses())}
      {shouldRenderShippingRates && (
        <div className={classNames(styles["content-segment"], styles["shipping-rates-block"])}>{renderShippingRates(LayoutStyleTypes.TYPE_SM)}</div>
      )}
      {specifications && (
        <div className={styles["content-segment"]}>
          <h2 className={styles.title}>{t("jtsow6p79bxmpjox.specifications", "Specifications")}</h2>
          <SpecificationsList type={LayoutStyleTypes.TYPE_SM} options={specifications} />
        </div>
      )}
      {aboutContent}
      {features.product_related && (
        <div className={styles["content-segment"]}>
          <h2 className={styles.title}>{t("j10il6245v20kshl.related-summary", "Related products")}</h2>
          {/* renderRelated(LayoutStyleTypes.TYPE_SM) */}
        </div>
      )}
      <div className={classNames(styles["content-segment"], styles.transparent, styles["content-h"])}>
        <BenefitsList type={LayoutStyleTypes.TYPE_SM} />
      </div>
      <PageTags jsonld={jsonLdDataset} meta={metaDataset} seo={seoMarkupDataset} />
    </>
  );
};

export default ProductPageMobile;
